/**
 * useHr — Issue #176.
 *
 * Admin-side hooks for the HR module:
 *   - useHrEmployees: list / detail of employee master records.
 *   - useHrImports:   list / detail / row list / events / lifecycle mutations
 *                     for roster import batches.
 *
 * 红线：
 *   - 只使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须带 expectedVersion 走乐观锁。
 *   - mutation 必须使用后端返回值刷新本地状态；不在前端伪造已通过 / 已提交。
 *   - 错误以后端返回值转换；不静默吞掉 HTTP 错误。
 *   - 不展示 tenantId / orgId / cipher / hash 字段；只展示 last4 / 状态 / 行数。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// HR endpoints are tenant-scoped and require an authenticated session;
// the typed client must attach `Authorization: Bearer <accessToken>` via the
// shared `tokenStorage` flow (PR #119) so requests do not 401.
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ---------------------------------------------------------------------------
// Types re-exported from generated client
// ---------------------------------------------------------------------------

export type HrEmployeeDto = ApiComponents['schemas']['HrEmployeeDto'];
export type HrEmployeeListDto = ApiComponents['schemas']['HrEmployeeListDto'];
export type HrEmployeeImportDto =
  ApiComponents['schemas']['HrEmployeeImportDto'];
export type HrEmployeeImportListDto =
  ApiComponents['schemas']['HrEmployeeImportListDto'];
export type HrEmployeeImportRowDto =
  ApiComponents['schemas']['HrEmployeeImportRowDto'];
export type HrEmployeeImportRowListDto =
  ApiComponents['schemas']['HrEmployeeImportRowListDto'];
export type HrEmployeeImportEventDto =
  ApiComponents['schemas']['HrEmployeeImportEventDto'];
export type CreateHrEmployeeImportDto =
  ApiComponents['schemas']['CreateHrEmployeeImportDto'];
export type CreateHrEmployeeImportRowDto =
  ApiComponents['schemas']['CreateHrEmployeeImportRowDto'];
export type ResolveHrImportRowDto =
  ApiComponents['schemas']['ResolveHrImportRowDto'];
export type ApproveHrImportDto =
  ApiComponents['schemas']['ApproveHrImportDto'];
export type CommitHrImportDto =
  ApiComponents['schemas']['CommitHrImportDto'];
export type CancelHrImportDto =
  ApiComponents['schemas']['CancelHrImportDto'];

export type HrEmploymentStatus = HrEmployeeDto['employmentStatus'];
export type HrEmployeeImportStatus = HrEmployeeImportDto['status'];
export type HrImportRowValidationStatus = HrEmployeeImportRowDto['validationStatus'];
export type HrImportRowResolutionStatus = HrEmployeeImportRowDto['resolutionStatus'];
export type HrImportRowAction = HrEmployeeImportRowDto['rowAction'];

export const HR_IMPORT_STATUS_LIST: readonly HrEmployeeImportStatus[] = [
  'parsing',
  'pending_review',
  'approved',
  'committed',
  'failed',
  'canceled',
];

export const HR_IMPORT_STATUS_LABELS: Record<HrEmployeeImportStatus, string> = {
  parsing: '解析中',
  pending_review: '待审核',
  approved: '已审核',
  committed: '已导入',
  failed: '解析失败',
  canceled: '已撤销',
};

export const HR_EMPLOYMENT_STATUS_LIST: readonly HrEmploymentStatus[] = [
  'active',
  'on_leave',
  'resigned',
  'suspended',
];

export const HR_EMPLOYMENT_STATUS_LABELS: Record<HrEmploymentStatus, string> = {
  active: '在职',
  on_leave: '休假',
  resigned: '离职',
  suspended: '停职',
};

export const HR_ROW_VALIDATION_LABELS: Record<HrImportRowValidationStatus, string> = {
  valid: '合法',
  error: '错误',
  duplicate: '重复',
};

export const HR_ROW_RESOLUTION_LABELS: Record<HrImportRowResolutionStatus, string> = {
  pending: '待处理',
  fixed: '已修复',
  skipped: '已跳过',
};

export const HR_ROW_ACTION_LABELS: Record<HrImportRowAction, string> = {
  insert: '新增',
  update: '更新',
  skip: '跳过',
};

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

export interface HrApiError {
  message: string;
  code?: string;
  status?: number;
}

function toApiError(err: unknown, httpStatus?: number): HrApiError {
  if (httpStatus === 409) {
    return {
      message: '记录已被其他操作修改或状态不符，请刷新后重试',
      code: 'CONFLICT',
      status: httpStatus,
    };
  }
  if (httpStatus === 403) {
    return { message: '没有权限执行此操作', code: 'FORBIDDEN', status: httpStatus };
  }
  if (httpStatus === 400) {
    return {
      message: '请求参数不合法，请检查必填项与字段格式',
      code: 'BAD_REQUEST',
      status: httpStatus,
    };
  }
  if (httpStatus === 404) {
    return { message: '资源不存在或不可访问', code: 'NOT_FOUND', status: httpStatus };
  }
  if (err instanceof Error) {
    return { message: err.message, status: httpStatus };
  }
  return { message: String(err), status: httpStatus };
}

// ---------------------------------------------------------------------------
// Employee list hook
// ---------------------------------------------------------------------------

export interface ListEmployeeFilter {
  employeeNo?: string;
  department?: string;
  employmentStatus?: HrEmploymentStatus;
  nameContains?: string;
  page?: number;
  limit?: number;
}

export function useHrEmployees() {
  const [items, setItems] = useState<HrEmployeeDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchEmployees = useCallback(async (filter?: ListEmployeeFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {};
      if (filter?.employeeNo) query.employeeNo = filter.employeeNo;
      if (filter?.department) query.department = filter.department;
      if (filter?.employmentStatus) query.employmentStatus = filter.employmentStatus;
      if (filter?.nameContains) query.nameContains = filter.nameContains;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/employees',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { items, total, loading, error, fetchEmployees };
}

// ---------------------------------------------------------------------------
// Import list / lifecycle hooks
// ---------------------------------------------------------------------------

export interface ListImportFilter {
  status?: HrEmployeeImportStatus;
  createdBy?: string;
  page?: number;
  limit?: number;
}

export interface ListImportRowFilter {
  validationStatus?: HrImportRowValidationStatus;
  resolutionStatus?: HrImportRowResolutionStatus;
  errorsOnly?: boolean;
  page?: number;
  limit?: number;
}

export function useHrImports() {
  const [items, setItems] = useState<HrEmployeeImportDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchImports = useCallback(async (filter?: ListImportFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.createdBy) query.createdBy = filter.createdBy;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/imports',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImport = useCallback(
    async (id: string): Promise<HrEmployeeImportDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/imports/{id}',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const fetchImportRows = useCallback(
    async (
      id: string,
      filter?: ListImportRowFilter,
    ): Promise<HrEmployeeImportRowListDto | null> => {
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.validationStatus) query.validationStatus = filter.validationStatus;
        if (filter?.resolutionStatus) query.resolutionStatus = filter.resolutionStatus;
        if (filter?.errorsOnly) query.errorsOnly = filter.errorsOnly;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/imports/{id}/rows',
          { params: { path: { id }, query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const fetchImportEvents = useCallback(
    async (id: string): Promise<HrEmployeeImportEventDto[] | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/imports/{id}/events',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ? [...data] : null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const createImport = useCallback(
    async (
      dto: CreateHrEmployeeImportDto,
    ): Promise<HrEmployeeImportDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/imports',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const resolveImportRow = useCallback(
    async (
      id: string,
      rowId: string,
      dto: ResolveHrImportRowDto,
    ): Promise<HrEmployeeImportRowDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/imports/{id}/rows/{rowId}/resolve',
          { params: { path: { id, rowId } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const approveImport = useCallback(
    async (
      id: string,
      dto: ApproveHrImportDto,
    ): Promise<HrEmployeeImportDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/imports/{id}/approve',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const commitImport = useCallback(
    async (
      id: string,
      dto: CommitHrImportDto,
    ): Promise<HrEmployeeImportDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/imports/{id}/commit',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const cancelImport = useCallback(
    async (
      id: string,
      dto: CancelHrImportDto,
    ): Promise<HrEmployeeImportDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/imports/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  return {
    items,
    total,
    loading,
    error,
    fetchImports,
    fetchImport,
    fetchImportRows,
    fetchImportEvents,
    createImport,
    resolveImportRow,
    approveImport,
    commitImport,
    cancelImport,
  };
}
